/**
 * Maps tool-local item ids (e.g. "item-0") back to workspace UUIDs
 * when persisting session results.
 */

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isWorkspaceUuid(id: string): boolean {
  return UUID_RE.test(id);
}

export interface ItemIdResolverContext {
  sessionItemIds?: string[];
  toolSessionItems?: Array<{ id: string; name?: string }>;
}

/** Resolve a tool result id to a workspace item UUID, or null if unmapped. */
export function resolveWorkspaceItemId(
  resultId: string,
  resultName: string | undefined,
  ctx: ItemIdResolverContext,
): string | null {
  if (isWorkspaceUuid(resultId)) return resultId;

  const { sessionItemIds = [], toolSessionItems = [] } = ctx;

  if (resultName) {
    const byName = toolSessionItems.find(
      i => i.name === resultName && isWorkspaceUuid(i.id),
    );
    if (byName) return byName.id;
  }

  const indexMatch = /^item-(\d+)$/.exec(resultId);
  if (indexMatch) {
    const idx = Number(indexMatch[1]);
    const fromSession = sessionItemIds[idx];
    if (fromSession && isWorkspaceUuid(fromSession)) return fromSession;
    const fromTool = toolSessionItems[idx];
    if (fromTool?.id && isWorkspaceUuid(fromTool.id)) return fromTool.id;
  }

  // Internal IDs for V3 typed/isolated items (e.g. item_xxxxx, crit_xxxxx).
  // These can't be resolved to workspace UUIDs, but should pass through so
  // aggregate scoring still works for isolated sessions.
  if (/^(item_|crit_|evt_)/.test(resultId)) return resultId;

  return null;
}
